import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";
import { PageHero, SectionHeading, FinalCTA } from "@/components/sections";
import { Reveal, stagger, item } from "@/components/motion";
import { motion } from "framer-motion";
import { useAppointment } from "@/components/AppointmentContext";

export default function ServicePage({ slug, data }) {
  const { openDialog } = useAppointment();
  const path = `/${slug}`;
  const crumbs = [
    { name: "Home", path: "/" },
    { name: data.eyebrow, path },
  ];

  return (
    <>
      <SEO title={data.eyebrow} description={data.metaDesc} path={path}
        image={data.image}
        jsonLd={[orgSchema, breadcrumbSchema(crumbs)]} />

      <PageHero eyebrow={data.eyebrow} title={data.title} intro={data.intro}
        image={data.image} breadcrumb={crumbs} />

      {/* Overview + image */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <div className="relative">
                <div className="pointer-events-none absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-softblue to-transparent blur-2xl opacity-70" />
                <img src={data.image} alt={data.eyebrow}
                  className="relative aspect-[4/3] w-full rounded-[2rem] object-cover shadow-premium" />
              </div>
            </Reveal>
            <div>
              <SectionHeading eyebrow={`About ${data.eyebrow}`} title="What to expect" />
              <div className="mt-6 space-y-5">
                {data.overview.map((p, i) => (
                  <Reveal key={i} delay={i * 0.08}>
                    <p className="text-lg text-muted-foreground leading-relaxed">{p}</p>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.2}>
                <Button onClick={openDialog} data-testid="service-schedule-btn"
                  className="mt-8 rounded-full bg-teal hover:bg-teal-dark text-white h-13 px-7 hover:-translate-y-0.5 transition-transform">
                  Schedule Your Free 15-Minute Consultation <ArrowUpRight className="ml-1 h-4 w-4" />
                </Button>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-secondary py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <SectionHeading center eyebrow="How It Works" title="A clear, collaborative path forward." />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {data.steps.map((s, i) => (
              <motion.div key={s.t} variants={item}
                className="relative rounded-3xl border border-navy-100 bg-white p-7 shadow-soft">
                <div className="font-display text-4xl text-teal/30">0{i + 1}</div>
                <h3 className="mt-3 font-display text-xl text-navy">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits + Who it's for */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="Benefits" title="What you can gain." />
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {data.benefits.map((b, i) => (
                  <Reveal key={b} delay={i * 0.05}>
                    <div className="flex items-center gap-3 rounded-2xl border border-navy-100 bg-white p-4 shadow-soft">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-teal/10 text-teal"><Check className="h-4 w-4" /></span>
                      <span className="text-navy/85">{b}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <div>
              <SectionHeading eyebrow="Who It's For" title="This may be right for you if…" />
              <div className="mt-8 flex flex-wrap gap-3">
                {data.whoFor.map((w) => (
                  <span key={w} className="rounded-full border border-navy-100 bg-secondary px-5 py-2.5 text-navy/80">{w}</span>
                ))}
              </div>
              <div className="mt-8 rounded-3xl bg-navy p-8 text-white shadow-premium">
                <h3 className="font-display text-2xl">Not sure if this is the right fit?</h3>
                <p className="mt-3 text-white/70">That's completely okay. Reach out and we'll help you find the right path — no pressure, just guidance.</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button onClick={openDialog} className="rounded-full bg-teal hover:bg-teal-dark h-12 px-6">Book a Consultation</Button>
                  <Link to="/contact"><Button variant="outline" className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-6">Contact Us</Button></Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
